import { supabase } from "@/integrations/supabase/client";

// Cache for signed URLs to avoid re-fetching
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();

/**
 * Converts a GCS URL (gcs://bucket/path) to a playable signed URL
 * Also handles Supabase storage URLs
 */
export async function getPlayableUrl(fileUrl: string | undefined | null): Promise<string | null> {
  if (!fileUrl) return null;
  
  // If it's already an https URL that's not a gcs:// scheme, return as-is
  if (fileUrl.startsWith('https://') || fileUrl.startsWith('http://')) {
    return fileUrl;
  }
  
  // Check cache first
  const cached = signedUrlCache.get(fileUrl);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url;
  }
  
  try {
    // Call the generate-signed-download edge function
    const { data, error } = await supabase.functions.invoke('generate-signed-download', {
      body: {
        fileRef: fileUrl,
        expiresIn: 3600 // 1 hour expiry
      }
    });
    
    if (error) {
      console.error('[AudioUtils] Error generating signed URL:', error);
      return null;
    }
    
    if (data?.signedUrl) {
      // Cache the URL (expires in 55 minutes to be safe)
      signedUrlCache.set(fileUrl, {
        url: data.signedUrl,
        expiresAt: Date.now() + (55 * 60 * 1000)
      });
      return data.signedUrl;
    }
    
    return null;
  } catch (err) {
    console.error('[AudioUtils] Failed to get playable URL:', err);
    return null;
  }
}

/**
 * Clears the signed URL cache
 */
export function clearAudioUrlCache(): void {
  signedUrlCache.clear();
}
